<script lang="ts">
	/**
	 * DocsPage layout - Wraps markdown documentation pages with sidebar and navigation.
	 *
	 * Receives frontmatter props from mdsvex:
	 * - title: Page title
	 * - description: Short description for meta/previews
	 * - headings: Auto-extracted from rehype plugin (h2/h3 elements)
	 */
	import type { Snippet } from 'svelte';
	import { page } from '$app/state';
	import * as Sidebar from '$shared/components/ui/sidebar/index.js';
	import DocsHeader from '$lib/components/docs/DocsHeader.svelte';
	import DocsSidebar from '$lib/components/docs/DocsSidebar.svelte';
	import DocsTableOfContents from '$lib/components/docs/DocsTableOfContents.svelte';
	import DocsPagination from '$lib/components/docs/DocsPagination.svelte';
	import { getAdjacentPages } from '$lib/data/docs-data';

	interface TocItem {
		id: string;
		text: string;
		level: number;
	}

	interface Props {
		title: string;
		description?: string;
		headings?: TocItem[];
		children: Snippet;
	}

	let { title, description, headings = [], children }: Props = $props();

	const currentSlug = $derived(page.url.pathname.replace('/docs/', ''));
	const adjacentPages = $derived(getAdjacentPages(currentSlug));
</script>

<svelte:head>
	<title>{title} - Corral AI Docs</title>
	{#if description}
		<meta name="description" content={description} />
	{/if}
</svelte:head>

<div class="docs-theme h-screen bg-white flex flex-col">
	<!-- Top header bar -->
	<DocsHeader />

	<Sidebar.Provider class="flex-1 min-h-0">
		<div class="flex flex-1 h-full overflow-hidden">
			<!-- Sidebar navigation -->
			<DocsSidebar />

			<!-- Main content area -->
			<Sidebar.Inset class="flex-1 overflow-y-auto pr-4">
				<div class="flex px-6 lg:px-12 py-12">
					<!-- Article content -->
					<article class="flex-1 prose-docs" data-pagefind-body>
						<h1>{title}</h1>

						{@render children()}

						<!-- Previous/Next navigation -->
						<DocsPagination prev={adjacentPages.prev} next={adjacentPages.next} />
					</article>

					<!-- Table of Contents (desktop only) -->
					{#if headings.length > 0}
						<aside class="hidden xl:block w-[var(--docs-toc-width)] ml-12 shrink-0">
							<DocsTableOfContents {headings} />
						</aside>
					{/if}
				</div>
			</Sidebar.Inset>
		</div>
	</Sidebar.Provider>
</div>

<style>
	/* Minimal scrollbar styling for docs pages */
	:global(.docs-theme) {
		scrollbar-width: thin;
		scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
	}

	:global(.docs-theme *) {
		scrollbar-width: thin;
		scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
	}

	:global(.docs-theme ::-webkit-scrollbar) {
		width: 6px;
		height: 6px;
	}

	:global(.docs-theme ::-webkit-scrollbar-track) {
		background: transparent;
	}

	:global(.docs-theme ::-webkit-scrollbar-thumb) {
		background-color: rgba(0, 0, 0, 0.15);
		border-radius: 3px;
	}

	:global(.docs-theme ::-webkit-scrollbar-thumb:hover) {
		background-color: rgba(0, 0, 0, 0.25);
	}
</style>
